import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './AuthModal.css';

function AuthModal({ isOpen, onClose }) {
  const { signUp, confirmSignUp, signIn, forgotPassword, confirmResetPassword } = useAuth();
  const [mode, setMode] = useState('signin'); // signin, signup, verify, forgot, resetConfirm
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const resetForm = () => {
    setError('');
    setPassword('');
    setCode('');
    setNewPassword('');
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await signUp(email, password, name);
      setMode('verify');
      setPassword('');
    } catch (err) {
      setError(err.message || 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await confirmSignUp(email, code);
      // Auto sign in after verification
      await signIn(email, password || prompt('Enter your password to sign in:'));
      onClose();
    } catch (err) {
      if (err.message?.includes('password')) {
        setMode('signin');
        setError('Email verified! Please sign in.');
      } else {
        setError(err.message || 'Verification failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await signIn(email, password);
      onClose();
    } catch (err) {
      if (err.name === 'UserNotConfirmedException') {
        setMode('verify');
        setError('Please verify your email first');
      } else {
        setError(err.message || 'Sign in failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await forgotPassword(email);
      setMode('resetConfirm');
    } catch (err) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetConfirm = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await confirmResetPassword(email, code, newPassword);
      setMode('signin');
      setError('Password reset! Please sign in.');
      resetForm();
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-close" onClick={onClose}>&times;</button>

        {mode === 'signin' && (
          <>
            <h2>Sign In</h2>
            <p className="auth-subtitle">Sign in to download plugins</p>
            <form onSubmit={handleSignIn}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              {error && <p className="auth-error">{error}</p>}
              <button type="submit" className="auth-btn" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
            <div className="auth-links">
              <button onClick={() => { resetForm(); setMode('signup'); }}>Create account</button>
              <button onClick={() => { resetForm(); setMode('forgot'); }}>Forgot password?</button>
            </div>
          </>
        )}

        {mode === 'signup' && (
          <>
            <h2>Create Account</h2>
            <p className="auth-subtitle">Sign up for free downloads</p>
            <form onSubmit={handleSignUp}>
              <input
                type="text"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="Password (8+ chars, uppercase, lowercase, number)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
              {error && <p className="auth-error">{error}</p>}
              <button type="submit" className="auth-btn" disabled={loading}>
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
            </form>
            <div className="auth-links">
              <button onClick={() => { resetForm(); setMode('signin'); }}>Already have an account? Sign in</button>
            </div>
          </>
        )}

        {mode === 'verify' && (
          <>
            <h2>Verify Email</h2>
            <p className="auth-subtitle">Check your email for a verification code</p>
            <form onSubmit={handleVerify}>
              <input
                type="text"
                placeholder="Verification code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
                autoComplete="one-time-code"
              />
              {error && <p className="auth-error">{error}</p>}
              <button type="submit" className="auth-btn" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </form>
            <div className="auth-links">
              <button onClick={() => { resetForm(); setMode('signin'); }}>Back to sign in</button>
            </div>
          </>
        )}

        {mode === 'forgot' && (
          <>
            <h2>Forgot Password</h2>
            <p className="auth-subtitle">Enter your email to receive a reset code</p>
            <form onSubmit={handleForgotPassword}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
              {error && <p className="auth-error">{error}</p>}
              <button type="submit" className="auth-btn" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Code'}
              </button>
            </form>
            <div className="auth-links">
              <button onClick={() => { resetForm(); setMode('signin'); }}>Back to sign in</button>
            </div>
          </>
        )}

        {mode === 'resetConfirm' && (
          <>
            <h2>Reset Password</h2>
            <p className="auth-subtitle">Enter the code sent to your email</p>
            <form onSubmit={handleResetConfirm}>
              <input
                type="text"
                placeholder="Verification code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
              />
              <input
                type="password"
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
              />
              {error && <p className="auth-error">{error}</p>}
              <button type="submit" className="auth-btn" disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
            <div className="auth-links">
              <button onClick={() => { resetForm(); setMode('signin'); }}>Back to sign in</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AuthModal;
